import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useAnalytics } from '@context/analytics/analytics.context';
import useFeatureFlag from '@hooks/use-feature-flags';
import { useBucket } from '@context/bucket.context';
import { LDState } from '@components/bucketTest/types';
import { executeAfterPageviewIfSemPDP } from '@helpers/utils/analytics-utils';
import styles from './pdp-welcome-voucher-banner.module.scss';

type PdpWelcomeVoucherBannerProps = {
  bannerText: string;
  voucherCode: string;
  closeBanner: () => void;
};

const PdpWelcomeVoucherBanner: React.FC<PdpWelcomeVoucherBannerProps> = ({ bannerText, voucherCode, closeBanner }) => {
  const [isBannerClosing, setIsBannerClosing] = useState(false);
  const hasTrackedViewRef = useRef(false);
  const { scriptsEventData } = useAnalytics();
  const { sendAnalyticEvent } = useAnalyticEvents('pdp_top_banner');
  const { isSemPDP } = useFeatureFlag();
  const { isFeatureEnabled, state: ldStatus } = useBucket();
  const isLdReady = ldStatus === LDState.SUCCESS;
  const isSemPdpPageviewEnabled = isLdReady ? isFeatureEnabled((f) => f.enablePageviewStEventsQueue, false) : false;

  useEffect(() => {
    if (!scriptsEventData?.isTcEvents1Loaded || !scriptsEventData?.isTcEvents7Loaded || hasTrackedViewRef.current) {
      return;
    }

    const eventPayload = { label: voucherCode };
    hasTrackedViewRef.current = true;
    executeAfterPageviewIfSemPDP(
      () => {
        sendAnalyticEvent('welcome_offer_view', eventPayload);
      },
      isSemPDP,
      'ProductPage',
      'welcome_offer_view',
      eventPayload,
      'pdp_top_banner',
      isSemPdpPageviewEnabled,
    );
  }, [
    scriptsEventData?.isTcEvents1Loaded,
    scriptsEventData?.isTcEvents7Loaded,
    isSemPDP,
    isSemPdpPageviewEnabled,
    voucherCode,
    sendAnalyticEvent,
  ]);

  const handleClose = () => {
    setIsBannerClosing(true);
    setTimeout(() => {
      closeBanner();
      sendAnalyticEvent('welcome_offer_close', { label: voucherCode });
    }, 300);
  };

  const { t } = useTranslation('default-layout');
  return (
    <div
      className={clsx(styles.pdpWelcomeVoucherBanner, {
        [styles.hidden]: isBannerClosing,
        [styles.textBackground]: isSemPDP,
      })}
    >
      <div className={styles.pdpWelcomeVoucherBanner__text}>{bannerText}</div>
      <button
        data-cy="pdp-banner-close-button"
        className={styles.pdpWelcomeVoucherBanner__closeBtn}
        type="button"
        onClick={handleClose}
        aria-label={t('MODAL.BUTTON.CLOSE', 'Close')}
      >
        <Icon
          name="close-default"
          ariaHidden
        />
        <span className="visuallyhidden">{t('MODAL.BUTTON.CLOSE')}</span>
      </button>
    </div>
  );
};

export default PdpWelcomeVoucherBanner;
